/**
 * Admin → Cron jobs.
 *
 * List of every cron-job row, grouped by name and scope. "Add" opens
 * the create modal; row click opens edit. "Sync to Prefect" calls the
 * sync endpoint and shows the diff in a toast.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { adminApi, type AdminCronJob } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import { CronJobModal } from "./CronJobModal";

const SCOPE_FILTERS = [
  { value: "", label: "All" },
  { value: "user", label: "User" },
  { value: "campaign", label: "Campaign" },
  { value: "agent", label: "Agent" },
  { value: "team", label: "Team" },
  { value: "project", label: "Project" },
];

function describeNotificationTarget(
  target: AdminCronJob["notification_target"],
): string {
  if (!target) return "scope default";
  if (target.type === "slack") {
    const id = String(target.config?.channel_id ?? "");
    return id ? `slack ${id}` : "slack (no channel)";
  }
  return target.type;
}

export function CronJobsPage() {
  // Seeded from `?scope_type=…&q=…` so the employee drawer's "Manage in Cron
  // jobs" link lands pre-filtered; local state from there on.
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("q") ?? "");
  const [scopeFilter, setScopeFilter] = useState(
    searchParams.get("scope_type") ?? "",
  );
  const [editing, setEditing] = useState<AdminCronJob | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const toast = useToast();
  const confirm = useConfirm();

  const cronQuery = adminApi.cronJobs.list({
    scope_type: scopeFilter || undefined,
  });
  const sync = adminApi.cronJobs.sync();
  const remove = adminApi.cronJobs.delete();

  const filtered = useMemo(() => {
    const list = cronQuery.data ?? [];
    if (!filter.trim()) return list;
    const q = filter.toLowerCase();
    return list.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.scope_id.toLowerCase().includes(q) ||
        (r.scope_label ?? "").toLowerCase().includes(q),
    );
  }, [cronQuery.data, filter]);

  function onSync() {
    sync.mutate(undefined, {
      onSuccess: (res) => {
        const counts = [
          res.added.length && `${res.added.length} added`,
          res.updated.length && `${res.updated.length} updated`,
          res.deleted.length && `${res.deleted.length} deleted`,
          res.skipped.length && `${res.skipped.length} skipped`,
        ]
          .filter(Boolean)
          .join(" · ");
        toast.ok(`Synced to Prefect: ${counts || "no changes"}`);
      },
      onError: (err) =>
        toast.error(`Sync failed: ${(err as Error).message}`),
    });
  }

  function onToggleEnabled(row: AdminCronJob) {
    const m = adminApi.cronJobs.patch(row.id);
    m.mutate(
      { enabled: !row.enabled },
      {
        onSuccess: () => toast.ok(row.enabled ? "Disabled" : "Enabled"),
        onError: (err) => toast.error(err),
      },
    );
  }

  async function onDelete(row: AdminCronJob) {
    const ok = await confirm({
      title: `Delete cron job '${row.name}'?`,
      body: `Scope ${row.scope_type}:${row.scope_id}. The schedule stops at once.`,
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    remove.mutate(row.id, {
      onSuccess: () => toast.ok("Deleted"),
      onError: (err) => toast.error(err),
    });
  }

  return (
    <>
      <PageHeader
        title="Cron jobs"
        description="Scheduled Jarvis briefings — one row per (name × scope)."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-second"
              onClick={onSync}
              disabled={sync.isPending}
            >
              {sync.isPending ? "Syncing…" : "Sync to Prefect"}
            </button>
            <button
              type="button"
              className="btn btn-main"
              onClick={() => setAddOpen(true)}
            >
              Add cron
            </button>
          </div>
        }
      />

      <div className="flex items-end gap-4 mb-5">
        <div className="flex-1 max-w-md">
          <label className="label" htmlFor="cron-search">
            Search
          </label>
          <input
            id="cron-search"
            className="input"
            placeholder="filter by name or scope…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="cron-scope-filter">
            Scope
          </label>
          <select
            id="cron-scope-filter"
            className="input"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
          >
            {SCOPE_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">{filtered.length}</span>{" "}
          listed
        </div>
      </div>

      <QueryBoundary
        query={cronQuery}
        empty={
          <div className="empty-state">
            <div className="title">No cron jobs</div>
            <p className="hint">Add the first one with the button above.</p>
          </div>
        }
        isEmpty={() => filtered.length === 0}
      >
        {() => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Scope</th>
                  <th>Schedule</th>
                  <th>Output</th>
                  <th>Enabled</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="font-heading font-semibold text-accent hover:underline"
                        onClick={() => setEditing(row)}
                      >
                        {row.name}
                      </button>
                    </td>
                    <td className="text-sm">
                      <span className="tag-chip">{row.scope_type}</span>
                      <span className="ml-1 text-ink-medium">
                        {row.scope_label || row.scope_id}
                      </span>
                      {row.dispatch_mode === "per_member" ? (
                        <span
                          className="tag-chip ml-1"
                          title="Fans out one job per member, each in their own scope"
                        >
                          per member
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-mono text-sm">
                        {row.schedule || "—"}
                      </div>
                      {row.schedule_description ? (
                        <div className="text-xs text-ink-low">
                          {row.schedule_description}
                        </div>
                      ) : null}
                    </td>
                    <td className="text-sm">
                      {describeNotificationTarget(row.notification_target)}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => onToggleEnabled(row)}
                        className="cursor-pointer"
                      >
                        <Pill value={row.enabled ? "active" : "paused"} />
                      </button>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditing(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-danger"
                          onClick={() => onDelete(row)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      <CronJobModal open={addOpen} onClose={() => setAddOpen(false)} />
      <CronJobModal
        open={!!editing}
        onClose={() => setEditing(null)}
        editing={editing}
      />
    </>
  );
}
